import { rgb } from "pdf-lib";
import {
  FARS_DFARS_2026_INFO,
  getFarsDfars2026Initiatives,
} from "./certificateBundles";
import {
  createPdfDocument,
  drawHeading,
  drawTextBlock,
  downloadPdfBytes,
} from "./pdfText";

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 50;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

export const buildFarsDfarsReportEmailHtml = (companyName: string): string => {
  const certCount = getFarsDfars2026Initiatives().length;
  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: Arial, sans-serif; color: #1f2937; line-height: 1.5;">
  <p style="font-size: 16px;">Dear ${companyName} Safety Team,</p>
  <p style="font-size: 15px;">Your weekly <strong>FARS/DFARS 2026 Safety Readiness</strong> summary is attached. Effective <strong>${FARS_DFARS_2026_INFO.effectiveDate}</strong>, federal contractor performance assessments place greater emphasis on documented employee safety training and workforce protection programs.</p>
  <p style="font-size: 15px;"><strong>This period:</strong></p>
  <ul style="font-size: 14px;">
    <li>OnMyWay protection features required: <strong>${certCount}</strong></li>
    <li>Status: Training completion tracked via OnMyWay (internal readiness — not government-issued credentials)</li>
  </ul>
  <p style="font-size: 14px;">Please review the attached FARS/DFARS Readiness Report and assign outstanding OnMyWay Training Completion certifications through the Employer Dashboard.</p>
  <p style="font-size: 13px; color: #6b7280;">${FARS_DFARS_2026_INFO.disclaimer}</p>
  <p style="font-size: 14px;">— OnMyWay Safety Platform<br/>SAFETY. ANYWHERE. ANYTIME.</p>
</body>
</html>`;
};

export const generateFarsDfarsReportPdf = async (
  companyName: string
): Promise<Uint8Array> => {
  const { pdfDoc, font, boldFont } = await createPdfDocument();
  const farsDfarsCerts = getFarsDfars2026Initiatives();

  let page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  let y = PAGE_HEIGHT - MARGIN;

  page.drawText("FARS/DFARS 2026 SAFETY READINESS REPORT", {
    x: MARGIN,
    y,
    size: 16,
    font: boldFont,
    color: rgb(0.05, 0.2, 0.45),
  });
  y -= 24;

  page.drawText(companyName, {
    x: MARGIN,
    y,
    size: 13,
    font: boldFont,
    color: rgb(0.1, 0.1, 0.15),
  });
  y -= 18;

  page.drawText(`Report generated: ${new Date().toLocaleDateString("en-US")}`, {
    x: MARGIN,
    y,
    size: 9,
    font,
    color: rgb(0.4, 0.4, 0.45),
  });
  y -= 20;

  y = drawTextBlock(FARS_DFARS_2026_INFO.summary, {
    page,
    font,
    x: MARGIN,
    y,
    maxWidth: CONTENT_WIDTH,
    fontSize: 10,
    lineHeight: 13,
  });
  y -= 8;

  y = drawTextBlock(FARS_DFARS_2026_INFO.disclaimer, {
    page,
    font,
    x: MARGIN,
    y,
    maxWidth: CONTENT_WIDTH,
    fontSize: 8,
    lineHeight: 11,
    color: rgb(0.45, 0.45, 0.5),
  });
  y -= 18;

  y = drawHeading(
    page,
    boldFont,
    `Required OnMyWay protection features (${farsDfarsCerts.length}) — FARS/DFARS 2026 alignment`,
    MARGIN,
    y,
    12
  );

  farsDfarsCerts.forEach((cert, index) => {
    if (y < MARGIN + 40) {
      page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
      y = PAGE_HEIGHT - MARGIN;
    }
    y = drawTextBlock(`${index + 1}. ${cert.title}`, {
      page,
      font,
      x: MARGIN + 4,
      y,
      maxWidth: CONTENT_WIDTH - 4,
      fontSize: 9,
      lineHeight: 12,
      bottomMargin: MARGIN,
    });
    y = drawTextBlock(cert.subtitle, {
      page,
      font,
      x: MARGIN + 12,
      y,
      maxWidth: CONTENT_WIDTH - 12,
      fontSize: 8,
      lineHeight: 11,
      bottomMargin: MARGIN,
      color: rgb(0.35, 0.35, 0.4),
    });
    y -= 4;
  });

  const pages = pdfDoc.getPages();
  pages.forEach((p, index) => {
    p.drawText(
      `OnMyWay FARS/DFARS Readiness Report • ${companyName} • Page ${index + 1}/${pages.length}`,
      {
        x: MARGIN,
        y: 28,
        size: 7,
        font,
        color: rgb(0.5, 0.5, 0.5),
      }
    );
  });

  return pdfDoc.save();
};

export const downloadFarsDfarsReportPdf = async (companyName: string) => {
  const bytes = await generateFarsDfarsReportPdf(companyName);
  const safeName = companyName.replace(/\s+/g, "_");
  downloadPdfBytes(bytes, `${safeName}_FARS_DFARS_2026_Readiness_Report.pdf`);
};
